import React, { useEffect, useMemo, useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import { InputNumber } from 'primereact/inputnumber';
import Container from 'saur_ui_library/src/Atoms/Container/Container';
import Word from 'saur_ui_library/src/Atoms/Word/Word';
import Button from 'saur_ui_library/src/Atoms/Input/Button/Button';
import { EcpDetailApi } from '../../utils/api';
import {
  normalizeStepReferenceFlow,
} from '../../utils/helpers/view/ecpDetailHelper';

type EcpStepInstallParamPopupProps = {
  row: any;
};

const EcpStepInstallParamPopup = ({ row }: EcpStepInstallParamPopupProps) => {
  const installCode = useMemo(() => row?.Code || '', [row]);
  const installationLabel = row?.Description || 'NR';
  const installationCode = row?.Code || 'NR';

  const [referenceFlowValue, setReferenceFlowValue] = useState<number | null>(0);
  const [isLoadingValue, setIsLoadingValue] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const roundOneDecimal = (value: number) => {
    return Math.round(value * 10) / 10;
  };

  const fetchSavedReferenceFlow = async () => {
    if (!installCode) {
      setReferenceFlowValue(0);
      setIsLoadingValue(false);
      return;
    }

    setIsLoadingValue(true);
    setErrorMessage('');
    try {
      const response = await EcpDetailApi.getStepReferenceFlow(
        installCode,
      );
      const rawValue =
        response.data?.referenceFlow ?? response.data?.VECSRef ?? 0;
      const normalizedValue = normalizeStepReferenceFlow(rawValue);
      const defaultedValue =
        normalizedValue !== null && normalizedValue >= 0 ? normalizedValue : 0;
      setReferenceFlowValue(roundOneDecimal(defaultedValue));
    } catch (error: any) {
      setReferenceFlowValue(0);
      if (error?.response?.status !== 404) {
        setErrorMessage('Impossible de charger la valeur en base. La valeur 0,0 est appliquée par défaut.');
      }
    } finally {
      setIsLoadingValue(false);
    }
  };

  useEffect(() => {
    fetchSavedReferenceFlow();
  }, [installCode]);

  const handleSave = async () => {
    setErrorMessage('');
    setSuccessMessage('');

    if (referenceFlowValue === null || !Number.isFinite(referenceFlowValue) || referenceFlowValue < 0) {
      setErrorMessage('Le débit de référence doit être un nombre positif ou nul.');
      return;
    }

    const rounded = roundOneDecimal(referenceFlowValue);

    if (!installCode) {
      setErrorMessage('Code installation STEP manquant.');
      return;
    }

    setIsSaving(true);
    try {
      await EcpDetailApi.updateStepReferenceFlow(installCode, rounded);
      setReferenceFlowValue(rounded);
      setSuccessMessage('Débit de référence enregistré.');
    } catch (error) {
      setErrorMessage('Impossible d’enregistrer le débit de référence.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container className='ecp-step-popup sds-reset-style'>
      <Styles />

      <Word
        className='step-popup-title'
        variant='span'
        textStyle='title'
        value='Paramètres STEP'
      />

      <Container className='step-popup-installation-card'>
        <Word
          className='step-popup-installation-value'
          variant='span'
          textStyle='normalReg'
          value={`Installation : ${installationLabel}`}
        />
        <Word
          className='step-popup-code-value'
          variant='span'
          textStyle='normalReg'
          value={`Code : ${installationCode}`}
        />
      </Container>

      <Container className='step-popup-field-block'>
        <Word
          className='step-popup-field-label'
          variant='span'
          textStyle='normalBold'
          value='Débit de référence (m³/j)'
        />
        <Container className='step-popup-input-row'>
          <InputNumber
            id='step-reference-flow-input'
            className='step-reference-flow-input'
            value={referenceFlowValue}
            onValueChange={(event: any) => {
              setReferenceFlowValue(event?.value ?? null);
              setErrorMessage('');
              setSuccessMessage('');
            }}
            mode='decimal'
            min={0}
            minFractionDigits={1}
            maxFractionDigits={1}
            disabled={isLoadingValue || isSaving}
          />

          <Word
            className='step-popup-field-help'
            variant='span'
            textStyle='normalReg'
            value='Valeur décimale positive ou nulle.'
          />
        </Container>
      </Container>

      <Container className='step-popup-actions'>
        <Button
          type='button'
          mode='plain'
          className='step-popup-save-btn'
          onClick={handleSave}
          disabled={isLoadingValue || isSaving}
        >
          <span className={isSaving ? 'step-popup-spinner' : 'step-popup-spinner hidden'} />
          <Word
            variant='span'
            textStyle='normalBold'
            value={isSaving ? 'Enregistrement...' : 'Enregistrer'}
          />
        </Button>
      </Container>

      {isLoadingValue && (
        <Word
          className='step-popup-info-message'
          variant='span'
          textStyle='normalReg'
          value='Chargement de la valeur enregistrée...'
        />
      )}

      {errorMessage !== '' && (
        <Word
          className='step-popup-error-message'
          variant='span'
          textStyle='normalReg'
          value={errorMessage}
        />
      )}

      {successMessage !== '' && (
        <Word
          className='step-popup-success-message'
          variant='span'
          textStyle='normalReg'
          value={successMessage}
        />
      )}
    </Container>
  );
};

const Styles = createGlobalStyle`
.ecp-step-popup.sds-reset-style {
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-x: hidden;

  .step-popup-title {
    color: #243977;
    font-size: 22px;
    line-height: 28px;
    margin-bottom: 0;
  }

  .step-popup-installation-card {
    border: none;
    border-radius: 10px;
    background: #edf2fa;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .step-popup-installation-value,
  .step-popup-code-value {
    color: #25314a;
    line-height: 20px;
    word-break: break-word;
  }

  .step-popup-field-block {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 2px auto 0;
    width: fit-content;
    max-width: 100%;
  }

  .step-popup-input-row {
    display: grid;
    grid-template-columns: 250px auto;
    align-items: center;
    column-gap: 12px;
    row-gap: 6px;
  }

  .step-popup-field-label {
    color: #243977;
    font-size: 15px;
    align-self: flex-start;
  }

  .step-reference-flow-input {
    width: 250px;
  }

  .step-reference-flow-input input.p-inputtext.p-component.p-inputnumber-input {
    background: #ffffff;
    border: 1px solid #b7c4d6;
    border-radius: 8px;
    height: 40px;
    padding: 8px 10px;
    font-size: 15px;
    color: #1b2b44;
    text-align: center;
  }

  .step-reference-flow-input.p-disabled input.p-inputtext.p-component.p-inputnumber-input {
    background: #f5f7fb;
  }

  .step-popup-field-help {
    color: #4c6583;
    font-size: 11px;
    line-height: 16px;
    white-space: nowrap;
    text-align: left;
    opacity: 0.9;
  }

  @media (max-width: 860px) {
    .step-popup-input-row {
      grid-template-columns: 1fr;
    }

    .step-popup-field-help {
      white-space: normal;
    }
  }

  .step-popup-actions {
    margin-top: 6px;
    align-items: center;
  }

  .step-popup-save-btn {
    min-height: 38px;
    min-width: 150px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .step-popup-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #ffffff;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    animation: step-popup-spin 0.9s linear infinite;
  }

  .step-popup-spinner.hidden {
    display: none;
  }

  .step-popup-info-message {
    color: #4c6583;
    font-size: 12px;
    line-height: 18px;
    margin-top: -4px;
  }

  .step-popup-error-message {
    color: #c62828;
    font-size: 12px;
    line-height: 18px;
    margin-top: -4px;
    align-self: center;
    text-align: center;
    max-width: 90%;
  }

  .step-popup-success-message {
    color: #2e7d32;
    font-size: 12px;
    line-height: 18px;
    margin-top: -4px;
  }
}

@keyframes step-popup-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
`;

export default EcpStepInstallParamPopup;
